// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the gate
//  ----------------------------------------------------------------------------
//  Which surface a visitor gets: the landing film, the three questions the app
//  asks before the first entry, or the app itself.
//
//  The rule is literal — no session, no app — and this module is the whole of
//  it. It is deliberately tiny and deliberately its own file, because "who is
//  allowed in" is the kind of decision that rots when it is spread across the
//  screens that happen to need it.
//
//  TWO ANSWERS, IN ORDER, AND WHY THERE ARE TWO.
//
//    mdLandingGate()   synchronous, from the device alone. This is the answer
//                      that decides the FIRST FRAME. It may not touch the
//                      network: a round trip before the first paint means
//                      showing somebody one surface on their way to the other,
//                      and a flash of the wrong thing is worse than a wait.
//
//    mdGateSettle()    asynchronous, once the server has been asked. This is
//                      the answer that is actually true, and it either confirms
//                      the first one or replaces it.
//
//  THE CASE THIS FILE EXISTS FOR. `mdAuthStatus()` reports `reachable` as well
//  as `signedIn`, and the two are not the same fact. A server that answers "no
//  session" has closed the door. A server that cannot be reached has said
//  nothing at all — and an app that treats silence as a refusal would meet
//  somebody signed in for months, on a plane, with a landing page and no way
//  past it. So an unreachable check changes nothing; the device's memory stands
//  until something contradicts it.
//
//  THE THIRD ANSWER. An account is required to use ManDana at all, and before
//  the first entry it is asked for a name, a date of birth and a gender. So
//  "signed in" and "may come in" stopped being the same fact: a device with a
//  session but no completed profile gets `onboarding`, which is drawn on the
//  landing side — the film stays behind it, unchanged, because the front door
//  must never reveal how many accounts a device is holding.
//
//  BOTH OF THE OTHER ANSWERS STILL BOOT. Onboarding writes a document, so it
//  needs the same open store the app does. Only `landing` is cold, which is what
//  lets md-boot.js ask this question before it has chosen a room at all: the
//  landing answer is exact from the session marker alone, and the difference
//  between the other two is settled again in Mount once the room is hydrated
//  and the profile can honestly be read.
//
//  AND ONE OUTCOME THAT IS NOT A SURFACE. When the settled answer is that the
//  account itself has been deleted — from another device, by the person who
//  owns it — this file is where that lands, because it is the one place that
//  asks the server who we are. It destroys this device's copy on the way to the
//  film. The alternative is a promise ("deleting your account removes it from
//  every device") kept only on the device that happened to press the button.
//
//  Routing is state, not URL. There is one address, as there always was.
// ════════════════════════════════════════════════════════════════════════════

/** Which surface to draw. */
type MdGate = 'landing' | 'onboarding' | 'app';

(function (g: any) {
  'use strict';

  /**
   * The first-frame answer, from this device only. Never awaits, never fetches.
   *
   * A device with no remembered session gets the landing — which is also the
   * right answer for the very first visit anybody ever makes, where there is
   * nothing to remember and nothing to wait for.
   */
  function mdLandingGate(): MdGate {
    if (!(typeof g.mdHasSessionMark === 'function' && g.mdHasSessionMark())) return 'landing';
    return mdGateProfiled() ? 'app' : 'onboarding';
  }

  /**
   * Has this account given the app the little it asks for before first entry?
   *
   * Two sources, and the cheap one first. The marker is this device's own note
   * that it has seen the profile finished, which is the only answer available
   * before storage is hydrated. The document is the truth, and it is what
   * answers for a device that has just pulled a profile completed elsewhere —
   * without it, linking a second device would ask the same person the same three
   * questions again.
   *
   * EXPORTED, because it is not only the gate that has to answer this. Entering
   * from the landing page asks it again after the store is open, and it used to
   * ask the document alone — which is this file's own bug, in another building.
   * One spelling, so the two cannot drift apart again.
   */
  function mdGateProfiled(): boolean {
    if (typeof g.mdAccountProfiled === 'function' && g.mdAccountProfiled()) return true;
    return typeof g.mdPersonComplete === 'function' && g.mdPersonComplete() === true;
  }

  /* ── the name the server handed over ────────────────────────────────────────
     Nobody should ever be asked their name by this app: a verifier wrote it down
     to mint the invitation, it is the display half of the username, and the
     passkey carries it. So when the settle below is told the profile is
     unfinished, the same answer brings the name (server/auth.js), and it is kept
     here for whoever asks the three questions.

     REMEMBERED RATHER THAN WRITTEN, because this runs before a room is open —
     the account is only known at the end of the settle, and writing a profile
     field before mdAppBoot would write it into whatever store was standing.
     The landing writes it once it has opened the right one. */
  let handed: string | null = null;
  /** The name the last settled answer carried, or null if it carried none. */
  function mdGateName(): string | null { return handed; }

  /**
   * The settled answer, after asking the server — and the place the device's
   * marker is brought back into line with it.
   *
   * That upkeep lives HERE and not inside mdAuthStatus, which is a query that
   * mdSync makes on every sync; a marker maintained there would be a storage
   * write on a hot path forever. This runs once at boot and again after a
   * sign-in, which is exactly as often as the answer can change.
   */
  async function mdGateSettle(): Promise<MdGate> {
    if (typeof g.mdAuthStatus !== 'function') return mdLandingGate();
    const status = await g.mdAuthStatus({ profiled: true });

    // Unreachable is not an answer. Whatever the device remembered still holds,
    // and in particular the marker is NOT cleared — that is the plane.
    if (!status || status.reachable !== true) return mdLandingGate();

    if (status.signedIn) {
      g.mdMarkSession();
      /* And which account it is. A device that was signed in before rooms
         existed has no id of its own to offer, and this is the one moment it
         can be picked up for free — the check has just asked the server who we
         are, and the answer includes it. It also corrects a marker that has
         fallen out of step with the server, which is the truth about who is
         signed in and this device's memory is not. */
      if (typeof g.mdMarkAccount === 'function' && typeof status.userId === 'string') {
        g.mdMarkAccount(status.userId);
      }
      /* And whether the three questions are still owed — asked of the SERVER,
         because this device may not be able to answer it.

         profiled() below reads a local marker and the local profile document,
         and both say "nothing answered" on a device whose store is empty: a new
         browser, a phone opening the site for the first time, storage evicted.
         Such a device used to be asked for a name its account had given long
         ago. Marwan hit it on staging — the server held his name, date of birth,
         gender and a picture, and his phone asked him what his name was.

         And the prompt is not merely an interruption. Profile fields merge by
         timestamp (app/md-person.ts), so an answer typed there carries a later
         clock than the real one and REPLACES it. Asking the question needlessly
         risks overwriting the answer.

         A `true` from the server OPENS the door and nothing else. It is
         deliberately not symmetric: a `false` does not override what this device
         knows, because a device that answered the questions while offline holds
         a complete profile the server has not been told about yet, and sending
         that person back to the questions would be this bug with the sides
         swapped. So the server can only ever turn 'onboarding' into 'app'.

         Marked while we are here, and AFTER mdMarkAccount because the marker
         belongs to the account's own compartment. Without it the first frame of
         every reload — answered by mdLandingGate() from the device alone — would
         paint the three questions and then take them away, which is worse than
         asking once. */
      /* The name rides with the answer, and it is taken whichever way the answer
         goes: a stale one remembered from an earlier boot would be a worse thing
         to write down than nothing at all. */
      handed = typeof status.name === 'string' && status.name.trim() ? status.name.trim() : null;
      if (status.profiled === true) {
        if (typeof g.mdMarkProfiled === 'function') g.mdMarkProfiled();
        return 'app';
      }
      return mdGateProfiled() ? 'app' : 'onboarding';
    }
    /* ── the account is gone, not merely closed ────────────────────────────
       A vanished session cannot tell these two apart on its own, so the device
       names the account it thinks it is holding and the server answers. The
       difference is the difference between locking a room and demolishing it,
       and it is wrong in both directions: treat an expiry as a deletion and
       somebody signs back in to an empty app, treat a deletion as an expiry
       and a person who asked for their data to be gone still has all of it on
       the device in their hand.

       `deleted` is only believed alongside a signed-OUT answer, which is the
       only coherent shape — deletion is a fact about an account that no longer
       exists — and it means this branch cannot be reached by a server that is
       confused about a live session. */
    if (status.deleted === true) { await mdGateEvicted(); return 'landing'; }

    /* An ordinary sign-out or expiry. The session marker goes, and NOTHING
       else: the room and everything in it stays exactly where it was, waiting
       for the same account to sign in again.

       The account marker stays too, deliberately, and it is what keeps the
       question above askable. A device that forgot which account it was
       holding could never afterwards be TOLD that account had been deleted,
       and would keep the room for ever. An explicit sign-out is different —
       mdSignOut() clears both, because somebody who chose to leave a shared
       device should not have an account id left on it. */
    g.mdForgetSession();
    return 'landing';
  }

  /**
   * This device's session has ended — it was not renewed, or somebody removed
   * this device from the account's list on another screen.
   *
   * ITS OWN FUNCTION, AND DELIBERATELY NOT mdGateEvicted. The account is fine;
   * only this device's key to it is gone. So the socket stops and the session
   * marker goes, and NOTHING ELSE: the room stays, the records stay, the
   * account marker stays. Signing out is a lock, not a demolition — sign back
   * in and everything is where it was, which is the whole difference between
   * this and the account being deleted.
   *
   * The account marker in particular has to survive, for the reason mdGateSettle
   * states above: a device that forgot which account it was holding could never
   * afterwards be told that account had been deleted.
   *
   * CALLED ONLY WHEN THE SERVER ACTUALLY SAID SO. `reachable` is what separates
   * "there is no session" from "nobody could be asked", and ejecting somebody on
   * the second would be locking a signed-in person out of ManDana on a plane.
   * Never throws.
   */
  async function mdGateSignedOut(): Promise<void> {
    /* The socket first, for the same reason as the demolition below: it is
       spoken with an identity that is about to stop meaning anything, and a
       room left open can still deliver a document into a page that is leaving. */
    if (typeof g.mdLiveStop === 'function') {
      try { g.mdLiveStop(); } catch (err) { /* the socket is going anyway */ }
    }
    if (typeof g.mdForgetSession === 'function') g.mdForgetSession();
    /* And the page goes, because every module-level cache in this app is still
       holding an account's documents and the surface behind them is the app.
       md-boot.js states the invariant: the first frame is decided from the
       session marker, and it now says landing. */
    if (typeof g.mdReloadApp === 'function') g.mdReloadApp();
  }

  /**
   * This account no longer exists. Take it off this device.
   *
   * Its own function because there is more than one way to find out — the
   * settle above, a sync that gets the same answer, a live frame from the
   * device that pressed the button, and the button itself — and four teardowns
   * that were nearly the same would be four that drifted apart. Every caller
   * comes through here. Never throws.
   */
  async function mdGateEvicted(): Promise<void> {
    /* The socket goes FIRST, for two reasons that both matter. Its farewell
       frame is spoken with this device's identity, which the forget below takes
       away; and a room left open during the demolition can still deliver a
       document into a store that is being torn down. */
    if (typeof g.mdLiveStop === 'function') {
      try { g.mdLiveStop(); } catch (err) { /* the socket is going anyway */ }
    }

    const id = typeof g.mdAccountId === 'function' ? g.mdAccountId() : null;
    if (id && typeof g.mdCompartmentDestroy === 'function') {
      /* Awaited, so the room is gone BEFORE anything reloads. A reload landing
         mid-demolition leaves a fraction of a deleted account behind with no
         second pass ever coming. Caught, because the demolition is best-effort
         by nature — another tab can hold a database open — and what must not
         happen is somebody left looking at an account that no longer exists
         because a cleanup failed. */
      try { await g.mdCompartmentDestroy(id); } catch (err) {
        if (g.console && g.console.warn) {
          g.console.warn('[ManDana] this account was deleted; some of its local data '
            + 'could not be removed and will be removed on the next visit —', err);
        }
      }
    }
    /* This device's name on the wire, and ONLY this account's copy of it. It is
       kept per account (app/md-device.ts) precisely so that signing out and
       back in is not a new device — but a deleted account's slot names an
       account that no longer exists, and nothing will ever answer to that id
       again. BEFORE the account mark goes, because the mark is how the slot is
       found; after it, there would be nothing to forget. */
    if (typeof g.mdDeviceForget === 'function') g.mdDeviceForget();
    if (typeof g.mdForgetAccount === 'function') g.mdForgetAccount();
    if (typeof g.mdForgetSession === 'function') g.mdForgetSession();
    /* And the page goes. Destroying the room SEALS the store (see
       mdCompartmentWipe), and every module-level cache in this app is still
       holding that account's documents — so continuing to run would be an app
       drawing a room that has been demolished under it. md-boot.js states the
       invariant this keeps: a page that has hydrated one room never hydrates
       another. */
    if (typeof g.mdReloadApp === 'function') g.mdReloadApp();
  }

  const api = {
    mdLandingGate, mdGateSettle, mdGateEvicted, mdGateSignedOut, mdGateProfiled, mdGateName,
  };
  Object.assign(g, api);

  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) mod.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
